import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar,
  Chip,
  Stack,
  Divider,
  useTheme,
  alpha,
  IconButton
} from '@mui/material';
import {
  School as SchoolIcon,
  People as PeopleIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Support as SupportIcon,
  Language as LanguageIcon,
  Home as HomeIcon,
  Restaurant as RestaurantIcon,
  DirectionsCar as DirectionsCarIcon,
  Assignment as AssignmentIcon,
  Timeline as TimelineIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as EmojiEventsIcon,
  LocationOn as LocationOnIcon,
  Group as GroupIcon,
  Computer as ComputerIcon,
  ThumbUp as ThumbUpIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useSEO } from '../hooks/useSEO';

// 导入图片
import heroImage from '../assets/images/main.jpg';
import teamHefei from '../assets/images/teams/team_1.jpg';
import teamJilongpo from '../assets/images/teams/team_2.jpg';
import teamLeader from '../assets/images/teams/leader.jpg';
import teamStyle from '../assets/images/teams/team_style.jpg';
import teacher1 from '../assets/images/teachers/image_56.jpg';
import teacher2 from '../assets/images/teachers/image_57.jpg';
import teacher3 from '../assets/images/teachers/image_58.jpg';
import schoolVisit1 from '../assets/images/school_visit_and_photos/image_49.jpg';
import schoolVisit2 from '../assets/images/school_visit_and_photos/image_50.jpg';
import physicalExam1 from '../assets/images/physical_examination_and_visa_renewal/image_29.jpg';
import physicalExam2 from '../assets/images/physical_examination_and_visa_renewal/image_30.jpg';
import physicalExam3 from '../assets/images/physical_examination_and_visa_renewal/image_31.jpg';
import physicalExam4 from '../assets/images/physical_examination_and_visa_renewal/image_32.jpg';
import physicalExam5 from '../assets/images/physical_examination_and_visa_renewal/image_33.jpg';
import physicalExam6 from '../assets/images/physical_examination_and_visa_renewal/image_34.jpg';
import document1 from '../assets/images/documents/image_35.jpg';
import document2 from '../assets/images/documents/image_36.jpg';
import document3 from '../assets/images/documents/image_37.jpg';
import document4 from '../assets/images/documents/image_38.jpg';
import goodService1 from '../assets/images/good_service/image_39.jpg';
import goodService2 from '../assets/images/good_service/image_40.jpg';
import goodService3 from '../assets/images/good_service/image_41.jpg';
import goodService4 from '../assets/images/good_service/image_42.jpg';
import goodService5 from '../assets/images/good_service/image_43.jpg';
import goodService6 from '../assets/images/good_service/image_44.jpg';
import goodService7 from '../assets/images/good_service/image_45.jpg';
import goodService8 from '../assets/images/good_service/image_46.jpg';
import goodService9 from '../assets/images/good_service/image_47.jpg';

const About = () => {
  const theme = useTheme();
  
  // 设置页面 SEO
  useSEO('about');

  // 轮播图状态
  const [physicalExamIndex, setPhysicalExamIndex] = useState(0);
  const [documentIndex, setDocumentIndex] = useState(0);
  const [goodServiceIndex, setGoodServiceIndex] = useState(0);
  const [schoolVisitIndex, setSchoolVisitIndex] = useState(0);

  // 图片数据
  const physicalExamImages = [
    { image: physicalExam1, alt: '体检换签1' },
    { image: physicalExam2, alt: '体检换签2' },
    { image: physicalExam3, alt: '体检换签3' },
    { image: physicalExam4, alt: '体检换签4' },
    { image: physicalExam5, alt: '体检换签5' },
    { image: physicalExam6, alt: '体检换签6' }
  ];

  const documentImages = [
    { image: document1, alt: '文书改版1' },
    { image: document2, alt: '文书改版2' },
    { image: document3, alt: '文书改版3' },
    { image: document4, alt: '文书改版4' }
  ];

  const goodServiceImages = [
    { image: goodService1, alt: '服务好评1' },
    { image: goodService2, alt: '服务好评2' },
    { image: goodService3, alt: '服务好评3' },
    { image: goodService4, alt: '服务好评4' },
    { image: goodService5, alt: '服务好评5' },
    { image: goodService6, alt: '服务好评6' },
    { image: goodService7, alt: '服务好评7' },
    { image: goodService8, alt: '服务好评8' },
    { image: goodService9, alt: '服务好评9' }
  ];

  const schoolVisitImages = [
    { image: schoolVisit1, alt: '探校实拍1' },
    { image: schoolVisit2, alt: '探校实拍2' }
  ];

  // 服务项目数据
  const services = [
    {
      icon: <AssignmentIcon />,
      title: '留学咨询',
      description: '专业的一对一留学规划咨询，根据学生个人情况制定最适合的留学方案'
    },
    {
      icon: <SchoolIcon />,
      title: '留学申请',
      description: '全程协助学生完成大学申请，确保申请材料的完整性和准确性'
    },
    {
      icon: <CheckCircleIcon />,
      title: '签证办理',
      description: '专业的签证申请指导，提高签证通过率，确保顺利获得留学签证'
    },
    {
      icon: <LanguageIcon />,
      title: '语言培训',
      description: '提供英语、马来语等语言课程培训，帮助学生快速提升语言能力'
    },
    {
      icon: <DirectionsCarIcon />,
      title: '接机接待',
      description: '提供机场接机服务，帮助学生安全抵达马来西亚并顺利入住'
    },
    {
      icon: <HomeIcon />,
      title: '住宿安排',
      description: '协助安排学生宿舍或租房，确保学生在马来西亚有舒适的居住环境'
    }
  ];

  // 核心优势数据
  const advantages = [
    {
      number: '10+',
      title: '年经验',
      description: '深耕马来西亚留学领域，拥有丰富的申请经验'
    },
    {
      number: '5000+',
      title: '成功案例',
      description: '已成功帮助超过5000名学生实现留学梦想'
    },
    {
      number: '100%',
      title: '个性化服务',
      description: '根据每个学生的特点制定专属留学方案'
    },
    {
      number: '24/7',
      title: '管家服务',
      description: '提供全天候的贴心服务，解决学生后顾之忧'
    }
  ];

  // 发展历程数据
  const timelineData = [
    {
      year: '2015',
      title: '大马留学团队成立，开始服务中国留学生；',
      description: '',
      icon: <GroupIcon />,
      color: 'primary'
    },
    {
      year: '2016',
      title: '积累申请和服务经验，加强与大马院校的合作沟通；',
      description: '',
      icon: <TrendingUpIcon />,
      color: 'secondary'
    },
    {
      year: '2017',
      title: '在国内正式成立安徽希航教育咨询有限公司；',
      description: '',
      icon: <BusinessIcon />,
      color: 'success'
    },
    {
      year: '2018',
      title: '在大马成立公司TOP MENTOR，全面提供境外服务;',
      description: '',
      icon: <PublicIcon />,
      color: 'info'
    },
    {
      year: '2019',
      title: '创建Bingoffer企业品牌，陆续拿到大马高校官方授权；',
      description: '',
      icon: <StarIcon />,
      color: 'warning'
    },
    {
      year: '2020',
      title: '国内留学团队落地安徽合肥，正式开展中马联合办公，为学生提供更全面的留学服务；',
      description: '',
      icon: <LocationOnIcon />,
      color: 'primary'
    },
    {
      year: '2021',
      title: '受疫情影响，开展线上留学咨询业务，服务海内外及全国各地的学生；',
      description: '',
      icon: <ComputerIcon />,
      color: 'secondary'
    },
    {
      year: '2022',
      title: '中马两地的服务团队规模不断扩大，在小红书、知乎、抖音等各个平台上积极分享留学讯息；',
      description: '',
      icon: <PeopleIcon />,
      color: 'success'
    },
    {
      year: '2023',
      title: '本硕博录取成功率保持在98%+，获得了众多学生及家长的好评；',
      description: '',
      icon: <EmojiEventsIcon />,
      color: 'info'
    },
    {
      year: '2024',
      title: '境外团队正式落地吉隆坡市中心，加强与各大马院校的线下合作沟通，希冀为希航的学生们提供更优质的留学服务及更满意的留学体验。',
      description: '',
      icon: <LocationOnIcon />,
      color: 'warning'
    },
    {
      year: '2025',
      title: '截至2025年，已拥有十年留学服务经验',
      description: '',
      icon: <ThumbUpIcon />,
      color: 'primary'
    }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* 英雄区域 */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: 8,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    mb: 3, 
                    fontWeight: 700,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  希航留学
                </Typography>
                <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                  马来西亚留学行业的佼佼者
                </Typography>
                <Typography variant="h6" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  10年留学申请经验，服务超过5000+学生，我们明白每一个学生都是不同的，
                  所以我们按照学生的个人情况规划留学方案，保证做到因才施教，实现学生的个性化发展。
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: theme.shadows[8],
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                    zIndex: 1
                  }
                }}
              >
                <img
                  src={heroImage}
                  alt="希航留学"
                  style={{
                    width: '100%',
                    height: '400px',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* 核心数据展示 */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            {advantages.map((advantage, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  sx={{ 
                    textAlign: 'center', 
                    p: 3,
                    height: '100%',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8]
                    }
                  }}
                >
                  <CardContent>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                      {advantage.number}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {advantage.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {advantage.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* 01 公司介绍 */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
            <Box sx={{ flexShrink: 0, mr: 2 }}>
              <Box sx={{ width: 23, height: 23 }}>
                <svg viewBox="0 0 46 38" style={{ display: 'block' }}>
                  <g>
                    <path d="M46,0H0L10,10H46V0Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,14H12L22,24H46V14Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,28H23L33,38H46V28Z" fill="#417dfe" fillRule="evenodd" />
                  </g>
                </svg>
              </Box>
            </Box>
            <Box sx={{ bgcolor: '#433cac', px: 3, py: 1, mx: 1 }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                公司介绍
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, ml: 2 }}>
              <Box sx={{ width: 23, height: 23 }}>
                <svg viewBox="0 0 46 38" style={{ display: 'block', transform: 'rotateY(180deg)' }}>
                  <g>
                    <path d="M46,0H0L10,10H46V0Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,14H12L22,24H46V14Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,28H23L33,38H46V28Z" fill="#417dfe" fillRule="evenodd" />
                  </g>
                </svg>
              </Box>
            </Box>
          </Box>
          
          <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
            <Typography variant="h5" sx={{ color: '#052391', fontWeight: 700, mb: 3 }}>
              安徽希航教育咨询有限公司
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 3, color: '#333' }}>
              （简称"希航教育"）成立于<strong style={{ color: '#bf0202' }}>2017</strong>年，是中国与马来西亚双注册认证的留学服务机构，
              专注马来西亚留学申请10年，累计帮助<strong style={{ color: '#bf0202' }}>5000+</strong>学子成功入读马来西亚顶尖院校。
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#333' }}>
              希航教育是集互联网平台+线下实体公司+境外实体公司为一体的留学机构。
              <strong style={{ color: '#052391' }}>中马两地联合办公</strong>的形式，
              境内团队为学生提供专业的留学服务，境外团队能够第一时间为抵达大马的学生给予帮助，
              旨在为学生带来"一站式"的留学体验。
            </Typography>
          </Paper>
        </Box>

        {/* Bingoffer品牌介绍 */}
        <Box sx={{ mb: 8 }}>
          <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
            <Typography variant="h5" sx={{ color: '#052391', fontWeight: 700, mb: 3 }}>
              Bingoffer品牌
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 3, color: '#333' }}>
              【bingoffer】是公司的重要教育品牌。
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 3, color: '#333' }}>
              主要为学生提供全程一站式的留学前期信息咨询、入学资格评估、定校择专业留学方案规划、背景提升、文书辅导、材料辅导、留学申请；
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 3, color: '#333' }}>
              留学中期签证申请、行前指导、接机住宿。
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 3, color: '#333' }}>
              安排留学后期学业生活、就业升学指导、回国学历认证等服务。
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#333' }}>
              服务端全程持续性，从择校到申请到入学生活，全方位一条龙服务，力图为每一位学子提供最真实、最专业的服务，寻求最优质的教育！
            </Typography>
          </Paper>
        </Box>

        {/* 02 团队介绍 */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
            <Box sx={{ flexShrink: 0, mr: 2 }}>
              <Box sx={{ width: 23, height: 23 }}>
                <svg viewBox="0 0 46 38" style={{ display: 'block' }}>
                  <g>
                    <path d="M46,0H0L10,10H46V0Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,14H12L22,24H46V14Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,28H23L33,38H46V28Z" fill="#417dfe" fillRule="evenodd" />
                  </g>
                </svg>
              </Box>
            </Box>
            <Box sx={{ bgcolor: '#433cac', px: 3, py: 1, mx: 1 }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                团队介绍
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, ml: 2 }}>
              <Box sx={{ width: 23, height: 23 }}>
                <svg viewBox="0 0 46 38" style={{ display: 'block', transform: 'rotateY(180deg)' }}>
                  <g>
                    <path d="M46,0H0L10,10H46V0Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,14H12L22,24H46V14Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,28H23L33,38H46V28Z" fill="#417dfe" fillRule="evenodd" />
                  </g>
                </svg>
              </Box>
            </Box>
          </Box>
          
          {/* 公司团队 */}
          <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
            <Typography variant="h5" sx={{ color: '#052391', fontWeight: 700, mb: 3 }}>
              公司团队
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#333' }}>
                    中国团队（合肥）：专业顾问提供定制化申请方案
                  </Typography>
                  <img
                    src={teamHefei}
                    alt="合肥团队"
                    style={{
                      width: '100%',
                      height: '300px',
                      objectFit: 'cover',
                      boxShadow: theme.shadows[4]
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    合肥团队
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#333' }}>
                    马来西亚团队（吉隆坡）：落地服务解决学习生活难题
                  </Typography>
                  <img
                    src={teamJilongpo}
                    alt="吉隆坡团队"
                    style={{
                      width: '100%',
                      height: '300px',
                      objectFit: 'cover',
                      boxShadow: theme.shadows[4]
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    吉隆坡团队
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* 团队成员 */}
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#052391' }}>
                    创始人
                  </Typography>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        mx: 'auto',
                        mb: 2,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        boxShadow: theme.shadows[4],
                        border: `4px solid ${theme.palette.primary.main}`
                      }}
                    >
                      <img
                        src={teamLeader}
                        alt="马超 - 希航教育创始人"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333' }}>
                    马超 | 希航教育创始人，马来西亚博特拉大学校友，深耕留学行业10年亲历马来西亚教育体系，精通院校招生逻辑，带领团队实现98%录取率，服务覆盖全马90%院校。
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#052391' }}>
                    申请团队
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2, color: '#333' }}>
                    希航教育的顾问老师，全部经过严格的专业培训，并且优先录用有多年留学经验或留学行业从业经验者，强调对顾问专业能力的不断提升、对学生的负责与耐心。
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333' }}>
                    针对学生需求匹配合适的留学方案，并且在其他服务团队的协助下全程把控学生的留学申请进度及保障顺利入学。
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#052391' }}>
                    境外团队
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2, color: '#333' }}>
                    境外团队提供一站式贴心服务：租房、一对一接机、陪同体检、协助注册选课、协助换签成功，以及协助解决学生入学后遇到的各类学习及生活上的问题。
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333' }}>
                    希航的境外团队每周都会前往各院校，积极跟进学生的申请状况，处理在申请时遇到的问题。
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* 03 业务范围 */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
            <Box sx={{ flexShrink: 0, mr: 2 }}>
              <Box sx={{ width: 23, height: 23 }}>
                <svg viewBox="0 0 46 38" style={{ display: 'block' }}>
                  <g>
                    <path d="M46,0H0L10,10H46V0Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,14H12L22,24H46V14Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,28H23L33,38H46V28Z" fill="#417dfe" fillRule="evenodd" />
                  </g>
                </svg>
              </Box>
            </Box>
            <Box sx={{ bgcolor: '#433cac', px: 3, py: 1, mx: 1 }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                业务范围
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, ml: 2 }}>
              <Box sx={{ width: 23, height: 23 }}>
                <svg viewBox="0 0 46 38" style={{ display: 'block', transform: 'rotateY(180deg)' }}>
                  <g>
                    <path d="M46,0H0L10,10H46V0Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,14H12L22,24H46V14Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,28H23L33,38H46V28Z" fill="#417dfe" fillRule="evenodd" />
                  </g>
                </svg>
              </Box>
            </Box>
          </Box>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#052391' }}>
                    留学申请服务
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 1 }}>
                      • 大马各高校本硕博阶段的留学申请服务；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 1 }}>
                      • 大马幼儿园至高中阶段的国际学校留学申请服务；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 1 }}>
                      • 其他服务：退转学、换专业、院校申诉、单签证、面试辅导、文书优化、RP指导、论文辅导；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333' }}>
                      • 语言培训，以外教一对一雅思口语课程为主。
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#052391' }}>
                    留学境外服务
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 1 }}>
                      • 大马公私立院校、海外分校及国际学校的探校服务
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 1 }}>
                      • 催签服务（1-3）工作日出val、出护照
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 1 }}>
                      • 接机租房
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 1 }}>
                      • 体检换签
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333' }}>
                      • 注册选课
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#052391' }}>
                    其他服务
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 1 }}>
                      • 旅游签、商务签办理
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 1 }}>
                      • UPM附近房屋租赁
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 1 }}>
                      • KL大马房产投资
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333' }}>
                      • 马来西亚wifi网络申请与安装（免费代办理）
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* 04 服务流程 */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
            <Box sx={{ flexShrink: 0, mr: 2 }}>
              <Box sx={{ width: 23, height: 23 }}>
                <svg viewBox="0 0 46 38" style={{ display: 'block' }}>
                  <g>
                    <path d="M46,0H0L10,10H46V0Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,14H12L22,24H46V14Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,28H23L33,38H46V28Z" fill="#417dfe" fillRule="evenodd" />
                  </g>
                </svg>
              </Box>
            </Box>
            <Box sx={{ bgcolor: '#433cac', px: 3, py: 1, mx: 1 }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                服务流程
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, ml: 2 }}>
              <Box sx={{ width: 23, height: 23 }}>
                <svg viewBox="0 0 46 38" style={{ display: 'block', transform: 'rotateY(180deg)' }}>
                  <g>
                    <path d="M46,0H0L10,10H46V0Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,14H12L22,24H46V14Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,28H23L33,38H46V28Z" fill="#417dfe" fillRule="evenodd" />
                  </g>
                </svg>
              </Box>
            </Box>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#052391' }}>
                    前期-递交申请
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 2 }}>
                      <strong>1.选校</strong><br />
                      根据学生的学历背景，语言成绩，预算，特殊要求等选择相关学校；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 2 }}>
                      <strong>2.选专业</strong><br />
                      与学生友好沟通后，根据意向和兴趣等选择适合的专业；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 2 }}>
                      <strong>3.准备材料</strong><br />
                      确定专业与学校后，跟踪指导学生准备材料，翻译文书，并协助学生解决准备材料过程中遇到的问题；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 2 }}>
                      <strong>4.针对研究型硕博</strong><br />
                      提供研究计划书格式和模板，对研究计划提出修改意见；匹配目标院校导师并套磁；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 2 }}>
                      <strong>5.协助面试</strong><br />
                      申请前后安排模拟面试，协助面试成功；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333' }}>
                      <strong>6.递交申请，拿到录取</strong><br />
                      向目标学校递交相关申请，跟进审核进度，协助学生取得offer。
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#052391' }}>
                    中期-办理签证
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 2 }}>
                      <strong>1.递交签证申请</strong><br />
                      及时查询签证进度和向学生更新相关情况，解决学生的疑惑和问题；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 2 }}>
                      <strong>2.上传文件</strong><br />
                      配合EMGS的相关要求，上传文件；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 2 }}>
                      <strong>3.协助签证办理</strong><br />
                      协助学生取得签证批准信、返签信(VAL)，也就是学生签证批文；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 2 }}>
                      <strong>4.签证跟进</strong><br />
                      解决签证中各个环节出现的问题，推进各个进度;
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333' }}>
                      <strong>5.协助贴签</strong><br />
                      协助学生完成贴签。
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#052391' }}>
                    后期-境外服务
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 2 }}>
                      <strong>1.接机</strong><br />
                      创建接机服务群，为学生提供一对一接机服务；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 2 }}>
                      <strong>2.协助入学</strong><br />
                      协助学生完成入境手续，新生注册及选课，了解周边环境等；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 2 }}>
                      <strong>3.宿舍/租房办理</strong><br />
                      协助预约学校宿舍或学校周边公寓租房；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333', mb: 2 }}>
                      <strong>4.疑难问题解答</strong><br />
                      为学生提供境外学习和生活的疑惑解答；
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333' }}>
                      <strong>5.中留服认证</strong><br />
                      毕业后协助学生回国办理中留服认证。
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* 服务特色 */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ textAlign: 'center', mb: 6, fontWeight: 600 }}>
            服务特色
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'success.main', 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 3,
                    fontSize: '2rem'
                  }}
                >
                  <PeopleIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  个性化规划
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  根据每个学生的个人情况、兴趣爱好和职业规划，
                  制定专属的留学方案，确保最适合的发展路径。
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'warning.main', 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 3,
                    fontSize: '2rem'
                  }}
                >
                  <SupportIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  管家式服务
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  从申请到入学，从学习到生活，我们提供全方位的管家式服务，
                  解决学生在马来西亚的所有后顾之忧。
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'info.main', 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 3,
                    fontSize: '2rem'
                  }}
                >
                  <StarIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  专业经验
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  10年行业经验，5000+成功案例，我们深知马来西亚留学的每一个细节，
                  能够为学生提供最专业的指导和建议。
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>


        {/* 发展历程 */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ textAlign: 'center', mb: 6, fontWeight: 600 }}>
            发展历程
          </Typography>
          <Box
            sx={{
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: { xs: '24px', md: '50%' },
                top: 0,
                bottom: 0,
                width: '4px',
                background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                transform: { xs: 'translateX(-2px)', md: 'translateX(-2px)' }
              }
            }}
          >
            {timelineData.map((item, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  mb: 6,
                  display: 'flex',
                  flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' },
                  alignItems: { xs: 'flex-start', md: 'center' }
                }}
              >
                {/* 时间线节点 */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: { xs: '16px', md: '50%' },
                    top: '24px',
                    transform: { xs: 'translateX(-50%)', md: 'translateX(-50%)' },
                    zIndex: 2
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: `${item.color}.main`,
                      width: 48,
                      height: 48,
                      fontSize: '1.2rem',
                      boxShadow: theme.shadows[4],
                      border: `4px solid ${theme.palette.background.paper}`
                    }}
                  >
                    {item.icon}
                  </Avatar>
                </Box>

                {/* 内容卡片 */}
                <Box
                  sx={{
                    width: { xs: '100%', md: '45%' },
                    ml: { xs: '60px', md: index % 2 === 0 ? '60px' : 0 },
                    mr: { xs: 0, md: index % 2 === 0 ? 0 : '60px' }
                  }}
                >
                  <Card
                    sx={{
                      p: 3,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette[item.color].main, 0.05)} 100%)`,
                      border: `1px solid ${alpha(theme.palette[item.color].main, 0.2)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[8]
                      }
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: `${item.color}.main`,
                          fontWeight: 700,
                          mb: 2,
                          fontSize: '1.1rem'
                        }}
                      >
                        {item.year}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: 'text.primary',
                          lineHeight: 1.6,
                          fontSize: '1rem'
                        }}
                      >
                        {item.title}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>


        {/* 07 服务好评 */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
            <Box sx={{ flexShrink: 0, mr: 2 }}>
              <Box sx={{ width: 23, height: 23 }}>
                <svg viewBox="0 0 46 38" style={{ display: 'block' }}>
                  <g>
                    <path d="M46,0H0L10,10H46V0Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,14H12L22,24H46V14Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,28H23L33,38H46V28Z" fill="#417dfe" fillRule="evenodd" />
                  </g>
                </svg>
              </Box>
            </Box>
            <Box sx={{ bgcolor: '#433cac', px: 3, py: 1, mx: 1 }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                服务好评
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, ml: 2 }}>
              <Box sx={{ width: 23, height: 23 }}>
                <svg viewBox="0 0 46 38" style={{ display: 'block', transform: 'rotateY(180deg)' }}>
                  <g>
                    <path d="M46,0H0L10,10H46V0Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,14H12L22,24H46V14Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,28H23L33,38H46V28Z" fill="#417dfe" fillRule="evenodd" />
                  </g>
                </svg>
              </Box>
            </Box>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#052391' }}>
                    体检换签
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: '#333' }}>
                    境外老师带学生体检
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconButton
                      onClick={() => setPhysicalExamIndex((prev) => (prev - 1 + physicalExamImages.length) % physicalExamImages.length)}
                      sx={{ position: 'absolute', left: 0, zIndex: 1, bgcolor: 'rgba(255,255,255,0.8)' }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Box sx={{ maxWidth: '100%' }}>
                      <img
                        src={physicalExamImages[physicalExamIndex].image}
                        alt={physicalExamImages[physicalExamIndex].alt}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block'
                        }}
                      />
                    </Box>
                    <IconButton
                      onClick={() => setPhysicalExamIndex((prev) => (prev + 1) % physicalExamImages.length)}
                      sx={{ position: 'absolute', right: 0, zIndex: 1, bgcolor: 'rgba(255,255,255,0.8)' }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                      </Box>
                  <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                    {physicalExamIndex + 1} / {physicalExamImages.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#052391' }}>
                    文书改版
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconButton
                      onClick={() => setDocumentIndex((prev) => (prev - 1 + documentImages.length) % documentImages.length)}
                      sx={{ position: 'absolute', left: 0, zIndex: 1, bgcolor: 'rgba(255,255,255,0.8)' }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Box sx={{ maxWidth: '100%' }}>
                      <img
                        src={documentImages[documentIndex].image}
                        alt={documentImages[documentIndex].alt}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block'
                        }}
                      />
                    </Box>
                    <IconButton
                      onClick={() => setDocumentIndex((prev) => (prev + 1) % documentImages.length)}
                      sx={{ position: 'absolute', right: 0, zIndex: 1, bgcolor: 'rgba(255,255,255,0.8)' }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                      </Box>
                  <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                    {documentIndex + 1} / {documentImages.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#052391' }}>
                    其他服务好评
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconButton
                      onClick={() => setGoodServiceIndex((prev) => (prev - 1 + goodServiceImages.length) % goodServiceImages.length)}
                      sx={{ position: 'absolute', left: 0, zIndex: 1, bgcolor: 'rgba(255,255,255,0.8)' }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Box sx={{ maxWidth: '100%' }}>
                      <img
                        src={goodServiceImages[goodServiceIndex].image}
                        alt={goodServiceImages[goodServiceIndex].alt}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block'
                        }}
                      />
                    </Box>
                    <IconButton
                      onClick={() => setGoodServiceIndex((prev) => (prev + 1) % goodServiceImages.length)}
                      sx={{ position: 'absolute', right: 0, zIndex: 1, bgcolor: 'rgba(255,255,255,0.8)' }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                      </Box>
                  <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                    {goodServiceIndex + 1} / {goodServiceImages.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* 08 团队风采 */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
            <Box sx={{ flexShrink: 0, mr: 2 }}>
              <Box sx={{ width: 23, height: 23 }}>
                <svg viewBox="0 0 46 38" style={{ display: 'block' }}>
                  <g>
                    <path d="M46,0H0L10,10H46V0Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,14H12L22,24H46V14Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,28H23L33,38H46V28Z" fill="#417dfe" fillRule="evenodd" />
                  </g>
                </svg>
              </Box>
            </Box>
            <Box sx={{ bgcolor: '#433cac', px: 3, py: 1, mx: 1 }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                团队风采
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, ml: 2 }}>
              <Box sx={{ width: 23, height: 23 }}>
                <svg viewBox="0 0 46 38" style={{ display: 'block', transform: 'rotateY(180deg)' }}>
                  <g>
                    <path d="M46,0H0L10,10H46V0Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,14H12L22,24H46V14Z" fill="#417dfe" fillRule="evenodd" />
                    <path d="M46,28H23L33,38H46V28Z" fill="#417dfe" fillRule="evenodd" />
                  </g>
                </svg>
              </Box>
            </Box>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#052391' }}>
                    团队风采
                  </Typography>
                  <Box sx={{ width: '100%' }}>
                    <img
                      src={teamStyle}
                      alt="团队风采"
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#052391' }}>
                    探校实拍
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconButton
                      onClick={() => setSchoolVisitIndex((prev) => (prev - 1 + schoolVisitImages.length) % schoolVisitImages.length)}
                      sx={{ position: 'absolute', left: 0, zIndex: 1, bgcolor: 'rgba(255,255,255,0.8)' }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Box sx={{ width: '100%' }}>
                      <img
                        src={schoolVisitImages[schoolVisitIndex].image}
                        alt={schoolVisitImages[schoolVisitIndex].alt}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block'
                        }}
                      />
                    </Box>
                    <IconButton
                      onClick={() => setSchoolVisitIndex((prev) => (prev + 1) % schoolVisitImages.length)}
                      sx={{ position: 'absolute', right: 0, zIndex: 1, bgcolor: 'rgba(255,255,255,0.8)' }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                      </Box>
                  <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                    {schoolVisitIndex + 1} / {schoolVisitImages.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>


        {/* 生活服务 */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ textAlign: 'center', mb: 6, fontWeight: 600 }}>
            生活服务保障
          </Typography>
          <Paper 
            sx={{ 
              p: 4, 
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
            }}
          >
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    <HomeIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    住宿安排
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                  协助安排学生宿舍或租房，确保学生在马来西亚有舒适的居住环境。
                  我们了解各个学校的住宿政策，能够为学生推荐最适合的住宿选择。
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    <DirectionsCarIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    交通出行
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                  提供交通出行指导，帮助学生熟悉马来西亚的公共交通系统，
                  包括地铁、公交、出租车等，确保学生出行便利。
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    <RestaurantIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    饮食适应
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                  帮助解决饮食不习惯的问题，推荐适合中国学生口味的餐厅，
                  提供饮食文化指导，让您的孩子在异国他乡也能感受到家乡的温暖。
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    <LanguageIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    文化融入
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                  提供马来西亚文化介绍和融入指导，帮助学生快速适应当地生活，
                  建立社交网络，享受丰富多彩的留学生活。
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* 联系我们 */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            选择希航留学，开启您的马来西亚留学之旅
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4 }}>
            我们承诺为每一位学生提供最专业、最贴心的留学服务
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            justifyContent="center"
            alignItems="center"
          >
            <Chip 
              label="10年专业经验" 
              color="primary" 
              size="large"
              icon={<StarIcon />}
            />
            <Chip 
              label="5000+成功案例" 
              color="secondary" 
              size="large"
              icon={<PeopleIcon />}
            />
            <Chip 
              label="24/7管家服务" 
              color="success" 
              size="large"
              icon={<SupportIcon />}
            />
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default About;
